/**
 * Game setup.
 *
 * Initial placement (per Camel Up rules):
 *   - Each of the 5 racing dice is rolled once; that camel is placed on the
 *     space matching the roll (1, 2, or 3). Stacking happens by roll order:
 *     if two camels land on the same space, the second goes on top.
 *   - The two crazy camels start at the end of the track. Each is rolled
 *     with its half of the grey die; black starts on space (16 + 1 - value),
 *     white likewise. They stack as encountered. (We use the grey die twice
 *     during setup, once per crazy camel.)
 *
 * Pure function — accepts an injectable RNG so tests are deterministic.
 */

import type {
  CamelColor,
  CamelUpPlayer,
  CamelUpState,
  CrazyColor,
  RacingColor,
} from '../types'
import type { PlayerColor } from '../../platform/types'
import {
  BOARD_SIZE,
  CRAZY_COLORS,
  DIE_MAX_VALUE,
  DIE_MIN_VALUE,
  MAX_PLAYERS,
  MIN_PLAYERS,
  PYRAMID_DICE,
  RACING_COLORS,
  STARTING_COINS,
  defaultLegBetStack,
} from './constants'

export interface SetupPlayer {
  uid: string
  displayName: string
  color: PlayerColor
  avatar: string
}

export interface SetupInput {
  /** Players in seating order. Length must be in `[MIN_PLAYERS, MAX_PLAYERS]`. */
  players: SetupPlayer[]
  /** Optional deterministic RNG returning [0, 1). Defaults to `Math.random`. */
  rng?: () => number
}

export function setupGame(input: SetupInput): CamelUpState {
  const { players, rng = Math.random } = input

  if (players.length < MIN_PLAYERS || players.length > MAX_PLAYERS) {
    throw new Error(
      `Camel Up requires ${MIN_PLAYERS}-${MAX_PLAYERS} players (got ${players.length}).`,
    )
  }
  // Reject duplicate uids.
  const uids = new Set<string>(players.map((p) => p.uid))
  if (uids.size !== players.length) {
    throw new Error('Duplicate uids in setup input.')
  }

  // Empty board.
  const spaces: Record<number, CamelColor[]> = {}
  for (let s = 1; s <= BOARD_SIZE; s++) spaces[s] = []

  // Racing camels: roll each die, drop the camel on the rolled space (1..3),
  // stacking in roll order.
  for (const color of RACING_COLORS) {
    const roll = rollD3(rng)
    spaces[roll].push(color)
  }

  // Crazy camels: each starts at the end of the track. We roll the grey die
  // once per crazy camel and place at space (BOARD_SIZE + 1 - roll), i.e.
  // 14/15/16 depending on the roll. Black first, then white — stacking is
  // by drop order onto the same space.
  for (const crazy of CRAZY_COLORS) {
    const roll = rollD3(rng)
    const startSpace = BOARD_SIZE + 1 - roll
    spaces[startSpace].push(crazy)
  }

  const playerRecord: Record<string, CamelUpPlayer> = {}
  for (const p of players) {
    playerRecord[p.uid] = {
      uid: p.uid,
      displayName: p.displayName,
      color: p.color,
      avatar: p.avatar,
      coins: STARTING_COINS,
      legBets: [],
      availableRaceCards: [...RACING_COLORS],
      pyramidTicketsThisLeg: 0,
    }
  }

  const legBetStacks: Record<RacingColor, ReturnType<typeof defaultLegBetStack>> = {
    red: defaultLegBetStack('red'),
    yellow: defaultLegBetStack('yellow'),
    green: defaultLegBetStack('green'),
    blue: defaultLegBetStack('blue'),
    purple: defaultLegBetStack('purple'),
  }

  return {
    seatOrder: players.map((p) => p.uid),
    activeIndex: 0,
    leg: 1,
    spaces,
    finished: [],
    rolledThisLeg: [],
    pyramidDice: [...PYRAMID_DICE],
    legBetStacks,
    racePiles: { winner: [], loser: [] },
    players: playerRecord,
    status: 'active',
    winner: null,
    legReview: null,
    raceReview: null,
  }
}

/** Roll a single die with uniform integer in [DIE_MIN_VALUE, DIE_MAX_VALUE]. */
export function rollD3(rng: () => number): 1 | 2 | 3 {
  const range = DIE_MAX_VALUE - DIE_MIN_VALUE + 1
  const v = DIE_MIN_VALUE + Math.floor(rng() * range)
  return v as 1 | 2 | 3
}

/** Pick black/white uniformly for the grey die's color half. */
export function rollCrazyColor(rng: () => number): CrazyColor {
  return rng() < 0.5 ? 'black' : 'white'
}
